/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-observers */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias, filterBy } from '@ember/object/computed';
import { service } from '@ember/service';
import AuTaxType from '@linc-technologies/linced-common/enums/au-tax-type';
import moment from 'moment';
import { isEmpty } from '@ember/utils';
import { observes } from '@ember-decorators/object';

export default class IndexController extends Controller {
  @action
  setItemAmount(value) {
    this.itemAmount = value;
  }

  @action
  setItemCostCentre(value) {
    this.itemCostCentre = value;
  }

  @action
  setItemDate(value) {
    this.itemDate = value;
  }

  @action
  setItemDescription(value) {
    this.itemDescription = value;
  }

  @action
  setItemDescriptionViolations(value) {
    this.itemDescriptionViolations = value;
  }

  @action
  setItemInternalOrder(value) {
    this.itemInternalOrder = value;
  }

  @action
  setItemIsDonation(value) {
    this.itemIsDonation = value;
  }

  @action
  setItemIsRefundable(value) {
    this.itemIsRefundable = value;
  }

  @action
  setItemIsTaxable(value) {
    this.itemIsTaxable = value;
  }

  @action
  setItemLedgerItem(value) {
    this.itemLedgerItem = value;
  }

  @action
  setItemName(value) {
    this.itemName = value;
  }

  @action
  setItemNameViolations(value) {
    this.itemNameViolations = value;
  }

  @action
  setItemPayerName(value) {
    this.itemPayerName = value;
  }

  @action
  setOutstanding(value) {
    this.outstanding = value;
  }

  @action
  setYear(value) {
    this.year = value;
  }

  queryParams = ['outstanding'];

  @service
  features;

  @service
  finance;

  @service
  personOptions;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  tax;

  @service
  toaster;

  @alias('session.school')
  school;

  // NSW DoE prohibited financial language (SMOS-012).
  // Populated by Finance::ProhibitedLanguageAlert via @onChange.
  @tracked
  itemNameViolations = [];

  @tracked
  itemDescriptionViolations = [];

  @computed('itemNameViolations.[]', 'itemDescriptionViolations.[]')
  get hasLanguageViolations() {
    return (
      !isEmpty(this.itemNameViolations) ||
      !isEmpty(this.itemDescriptionViolations)
    );
  }

  @tracked hideArchivedLineItems = false;

  @computed('model.items.[]', 'hideArchivedLineItems')
  get filteredItems() {
    if (this.hideArchivedLineItems) {
      return this.model.items.filter((item) => !item.isArchived);
    }
    return this.model.items;
  }

  @tracked
  outstanding = true;
  @tracked currentYear = moment().year();
  year = moment().year();

  get nextYear() {
    return this.currentYear + 1;
  }

  @computed('model.ledgers.[]', 'features.sapFinance')
  get availableLedgers() {
    if (!this.features.sapFinance) {
      // For most schools, simply filter out hidden Ledgers.
      return this.model.ledgers.filter((item) => !item.isHidden);
    }

    // For SAP schools, filter out hidden and inactive Ledgers.
    return this.model.ledgers.filter(
      (ledger) => ledger.isActive && !ledger.isHidden,
    );
  }

  @filterBy('model.costCentres', 'isActive')
  availableCostCentres;

  @filterBy('model.internalOrders', 'isActive')
  availableInternalOrders;

  @computed(
    'model.{ledgerItems.[],futureLedgerItems.[]}',
    'features.sapFinance',
  )
  get ledgerItems() {
    let ledgerItems = this.model.ledgerItems;
    if (!isEmpty(this.model.futureLedgerItems)) {
      ledgerItems = [
        ...this.model.ledgerItems.slice(),
        ...this.model.futureLedgerItems.slice(),
      ];
    }
    if (this.features.sapFinance) {
      // Filter to items that have been sent to SAP.
      ledgerItems = ledgerItems.filter((item) => item.nswSapOk);
    }
    return ledgerItems;
  }

  @tracked
  addItemDialogOpen = false;
  @tracked
  errorMessage = '';
  addItemMode = 'new';
  @tracked
  itemLedgerItem = null;
  @tracked
  itemLedger = null;
  @tracked
  itemCostCentre = null;
  @tracked
  itemInternalOrder = null;
  @tracked
  itemAmount = null;
  @tracked
  itemDate = moment().format('YYYY-MM-DD');
  @tracked
  itemDescription = '';
  @tracked
  itemName = '';
  @tracked
  itemIsTaxable = true;
  @tracked
  itemIsDonation = false;
  @tracked
  itemIsRefundable = true;
  @tracked
  itemPayerName = '';

  @tracked
  itemAddInProgress;

  @observes('itemLedgerItem', 'addItemMode')
  _resetAddItemFields() {
    if (this.addItemMode === 'new') {
      this.itemDate = moment().format('YYYY-MM-DD');
      this.itemDescription = '';
      this.itemName = '';
      this.itemAmount = null;
      this.itemLedger = null;
      this.itemCostCentre = null;
      this.itemInternalOrder = null;
      this.itemIsTaxable = true;
      this.itemIsDonation = false;
      this.itemIsRefundable = true;
    } else if (this.itemLedgerItem) {
      this.itemDate = this.itemLedgerItem.defaultDate;
      this.itemDescription = this.itemLedgerItem.defaultDescription;
      this.itemName = this.itemLedgerItem.defaultName;
      this.itemAmount = this.itemLedgerItem.defaultAmount;
      this.itemIsTaxable = this.itemLedgerItem.isTaxable;
      this.itemIsDonation = this.itemLedgerItem.isDonation;
      this.itemCostCentre = this.itemLedgerItem.costCentre;
      this.itemInternalOrder = this.itemLedgerItem.internalOrder;
      this.itemIsRefundable = this.itemLedgerItem.isRefundable;
    }
  }

  @action
  toggleHideArchivedLineItems(value) {
    this.hideArchivedLineItems = !value;
    this.personOptions.setHideArchivedLineItems(!value);
  }

  @action
  toggleAddItemDialog() {
    this.errorMessage = '';
    this.toggleProperty('addItemDialogOpen');
    if (!this.addItemDialogOpen) {
      this.resetAddItemForm();
    }
  }

  @action
  setAddItemMode(mode) {
    this.set('addItemMode', mode);
  }

  @action
  selectLedger(ledger) {
    this.itemLedger = ledger;
    this.itemInternalOrder = null;
  }

  @action
  async addItem() {
    let itemData = {
      description: this.itemDescription,
      communityPayerName: this.itemPayerName,
      date: this.itemDate,
      amount: this.itemAmount,
      isTaxable: this.itemIsTaxable,
      isDonation: this.itemIsDonation,
      isRefundable: this.itemIsRefundable,
    };
    switch (this.addItemMode) {
      case 'ledger':
        itemData.ledgerItemId = this.itemLedgerItem;
        // A belongsTo takes a record, not the async proxy.
        itemData.ledgerId = await this.itemLedgerItem.ledger;
        itemData.costCentreId = await this.itemLedgerItem.costCentreId;
        itemData.internalOrderId = await this.itemLedgerItem.internalOrderId;
        itemData.name = this.itemLedgerItem.name;
        break;
      case 'new':
        itemData.ledgerId = this.itemLedger;
        itemData.costCentreId = this.itemCostCentre;
        itemData.internalOrderId = this.itemInternalOrder;
        itemData.name = this.itemName;
        break;
    }

    let taxType = itemData.ledgerId.get('taxType');
    if (this.features.sapFinance) {
      itemData.taxType = taxType;
      itemData.isTaxable = taxType === AuTaxType.S1;
      itemData.isDonation = itemData.ledgerId.get('isDeductibleGiftReceipt');
    }

    if (itemData.isTaxable) {
      itemData.taxPercentage = this.tax.getRate(itemData.date, taxType) || 0;
    }

    this.store
      .createRecord('finance/line-item', itemData)
      .save()
      .then(() => {
        this.toaster.success('Line Item created successfully.');
        this.addItemDialogOpen = false;
        this.resetAddItemForm();
        this.model.items.update();
      })
      .catch(() => {
        this.toaster.error('Error creating Line Item!');
      })
      .then(() => {
        this.itemAddInProgress = false;
      });
  }

  resetAddItemForm() {
    this.setProperties({
      itemLedgerItem: null,
      itemLedger: null,
      itemAmount: null,
      itemIsTaxable: true,
      itemIsDonation: false,
      itemIsRefundable: true,
      itemCostCentre: null,
      itemInternalOrder: null,
      itemName: '',
      itemDescription: '',
      itemDate: moment().format('YYYY-MM-DD'),
      itemPayerName: '',
      addItemMode: 'new',
    });
  }
}
